import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DndContext, 
    DragOverlay,
    MouseSensor, 
    TouchSensor, 
    useSensor, 
    useSensors 
} from '@dnd-kit/core';
import { startOfDay, differenceInCalendarDays, addDays } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Box, useTheme, Popper, Fade } from '@mui/material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { fetchLegMembersByFlt } from 'state/api';
import { hasPermission } from 'state/checkPermission';
import CrewListDialog from 'components/CrewListDialog';
import { useWheelAxisLock } from 'hooks/useWheelAxisLock';
import Timeline from './Timeline';
import NowLine from './NowLine';
import DroppableRow from './DroppableRow';
import DraggableObject from './DraggableObject';
import RouteLinks from './RouteLinks';
import PopperContent from './PopperContent';


const RightPanel = ({
    syncX,
    syncY,
    timelineScrollRef,
    topLeftScrollRef,
    topRightScrollRef,
    bottomRightScrollRef,
    rowHeight = 50,
    cellWidth = 20,
    isShownCrewRoutes = false,
    data = [],
    changedIds = null,
}) => {
    const theme = useTheme();
    const user = useSelector((state) => state.user);
    const timeMode = useSelector((state) => state.timemode);
    const canView = useMemo(() => 
        hasPermission(user?.permissions, "FlightGanttChart", "view", false)
    , [user]);
    
    const { startDate, endDate } = useDateRangeContext();
    const { onWheel, onWheelEnd } = useWheelAxisLock();    

    const hasAutoScrolled = useRef(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [draggingId, setDraggingId] = useState(null);
    const isSelectingRef = useRef(false);
    const selectionRectRef = useRef(null);
    const [selectionRect, setSelectionRect] = useState(null); // { x1, y1, x2, y2 } — for rendering only

    const baseDate = useMemo(
        () => startDate ? startOfDay(new Date(startDate)) : null,
        [startDate]
    );
    const datesCount = useMemo(() => {
        if (!startDate || !endDate) return 0;
        return differenceInCalendarDays(addDays(endDate, 2), startDate);
    }, [startDate, endDate]);

    const rowCount = data?.length || 0;

    const gridDimensions = useMemo(() => ({
        width: datesCount * 24 * cellWidth,
        height: rowCount * rowHeight,
    }), [datesCount, cellWidth, rowCount, rowHeight]);
    
    const isInteracting = Boolean(draggingId) || selectionRect !== null;    
    const [dialogState, setDialogState] = useState({
        open: false,
        title: 'CREW LIST',
        flight: [],
        crewList: [],
        jumpList: []
    });
    
    const [localData, setLocalData] = useState(data);
    useEffect(() => {
        if (!endDate) return;
        setLocalData(data);
    }, [data, endDate]);

    useEffect(() => {
        const el = topRightScrollRef.current;
        if (!el) return;

        el.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            el.removeEventListener('wheel', onWheel);
        };
    }, [topRightScrollRef, onWheel]);
   
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setSelectedIds(new Set());
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);
        
        
    const selectObjectsInRect = useCallback((rect) => {
        const { x1, y1, x2, y2 } = rect;
        const minX = Math.min(x1, x2);
        const maxX = Math.max(x1, x2);
        const minY = Math.min(y1, y2);
        const maxY = Math.max(y1, y2);

        if (maxX - minX < 3 && maxY - minY < 3) return;

        const newSelectedIds = new Set();

        localData.forEach((row, rowIndex) => {
            const rowTop = rowIndex * rowHeight;
            const rowBottom = rowTop + rowHeight;

            if (rowBottom >= minY && rowTop <= maxY) {
                row.flights?.forEach(f => {
                    const fLeft = f.startSlot * cellWidth;
                    const fRight = fLeft + f.duration * cellWidth;
                    if (fRight >= minX && fLeft <= maxX) {
                        newSelectedIds.add(f.id);
                    }
                });
            }
        });

        setSelectedIds(newSelectedIds);
    }, [localData, rowHeight, cellWidth]);
    
    const activeFlight = useMemo(() => {
        if (!draggingId) return null;
        return localData
            .flatMap(r => r.flights || [])
            .find(f => f.id === draggingId);
    }, [draggingId, localData]);

    const [hoveredFlight, setHoveredFlight] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    
    const displayFlight = activeFlight || hoveredFlight;
    const finalAnchor = activeFlight 
        ? document.querySelector(`[data-object-id="${activeFlight.id}"]`) 
        : anchorEl;
   
    const handleMouseEnter = useCallback((event, object) => {
        setAnchorEl(event.currentTarget);
        setHoveredFlight(object);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setAnchorEl(null);
        setHoveredFlight(null);
    }, []);
    
    const handleMouseDown = useCallback((e) => {
        if (e.target !== e.currentTarget && !e.ctrlKey && !e.shiftKey && !e.metaKey) return;

        isSelectingRef.current = true;

        const scrollContainer = topRightScrollRef.current;
        if (!scrollContainer) return;

        const bounding = scrollContainer.getBoundingClientRect();
        const x = e.clientX - bounding.left + scrollContainer.scrollLeft;
        const y = e.clientY - bounding.top + scrollContainer.scrollTop;
        const rect = { x1: x, y1: y, x2: x, y2: y };

        selectionRectRef.current = rect;
        setSelectionRect(rect);
    }, [topRightScrollRef]);

    const handleMouseMove = useCallback((e) => {
        if (!isSelectingRef.current || !topRightScrollRef.current) return;

        requestAnimationFrame(() => {
            const scrollContainer = topRightScrollRef.current;
            if (!scrollContainer) return;
            const bounding = scrollContainer.getBoundingClientRect();
            const x = e.clientX - bounding.left + scrollContainer.scrollLeft;
            const y = e.clientY - bounding.top + scrollContainer.scrollTop;

            const next = selectionRectRef.current
                ? { ...selectionRectRef.current, x2: x, y2: y }
                : null;
            selectionRectRef.current = next;
            setSelectionRect(next);
        });
    }, [topRightScrollRef]);

    const handleMouseUp = useCallback(() => {
        const rect = selectionRectRef.current;
        selectionRectRef.current = null;
        setSelectionRect(null);

        if (rect) selectObjectsInRect(rect);

        requestAnimationFrame(() => {
            isSelectingRef.current = false;
        });
    }, [selectObjectsInRect]);

    const handleObjectClick = useCallback((object, event) => {
        setSelectedIds(prev => {
            const newSelection = new Set(prev);
            const isMultiSelect = event.ctrlKey || event.metaKey || event.shiftKey;

            if (isMultiSelect) {
                // Nếu đã chọn rồi thì bỏ chọn (toggle), nếu chưa thì thêm vào
                if (newSelection.has(object.id)) {
                    newSelection.delete(object.id);
                } else {
                    newSelection.add(object.id);
                }
            } else {
                // Click bình thường (không giữ phím): Chọn duy nhất 1 hoặc bỏ chọn nếu click lại
                if (newSelection.has(object.id) && newSelection.size === 1) {
                    newSelection.clear();
                } else {
                    newSelection.clear();
                    newSelection.add(object.id);
                }
            }
            return new Set(newSelection);
        });
    }, []);
    
    const handleBackgroundClick = useCallback(() => {
        if (isSelectingRef.current) return;
        setSelectedIds(new Set());
    }, []);

    const handleDragStart = useCallback((event) => {
        setDraggingId(event.active.id);
    }, []);
    
    const handleDragEnd = useCallback((event) => {
        const { active, over, delta } = event;
        setDraggingId(null);

        if (!over) return;

        const draggedObject = active.data.current?.object;
        if (!draggedObject) return;

        // Sử dụng trực tiếp selectedIds từ state của RightPanel
        // Thay vì dùng: const selectedIdsFromEvent = Array.from(active.data.current?.selectedIds || []);
        const currentSelectedArray = Array.from(selectedIds); 

        const hourDiff = delta.x / cellWidth;
        
        const sourceRowIdx = localData.findIndex(row => 
            row.flights?.some(f => f.id === draggedObject.id)
        );
        const targetRowIdx = localData.findIndex(row => row.id === over.id);
        const rowDiff = targetRowIdx - sourceRowIdx;

        // Xác định danh sách ID cần di chuyển
        // Nếu đối tượng bị cầm kéo nằm trong nhóm đang chọn -> kéo cả nhóm
        // Nếu đối tượng bị cầm kéo KHÔNG nằm trong nhóm đang chọn -> chỉ kéo mình nó
        const idsToMove = currentSelectedArray.includes(draggedObject.id) 
            ? currentSelectedArray 
            : [draggedObject.id];

        setLocalData(prev => {
            const movedObjectsMap = new Map();
            
            prev.forEach((row, currentIdx) => {
                row.flights?.forEach(f => {
                    if (idsToMove.includes(f.id)) {
                        const newRowIdx = Math.max(0, Math.min(prev.length - 1, currentIdx + rowDiff));
                        const targetRowId = prev[newRowIdx].id;
                        
                        movedObjectsMap.set(f.id, {
                            ...f,
                            startSlot: f.startSlot + hourDiff,
                            targetRowId: targetRowId
                        });
                    }
                });
            });

            return prev.map(row => {
                const remainingFlights = (row.flights || []).filter(f => !idsToMove.includes(f.id));
                const incomingFlights = Array.from(movedObjectsMap.values())
                    .filter(f => f.targetRowId === row.id)
                    .map(({ targetRowId, ...rest }) => rest);

                return {
                    ...row,
                    flights: [...remainingFlights, ...incomingFlights]
                };
            });
        });
    }, [cellWidth, localData, selectedIds]);

    const handleOpenCrewListDialog = useCallback(async (flight) => {
        try {
            if (!flight) return;

            const flightDate = flight.flightDate;
            const flightDesc = flight.flightDesc;
            const dep = flight.dep;
            
            const { 
                flightDetails, 
                legMembers, 
                jumpSeaters 
            } = await fetchLegMembersByFlt(flightDate, flightDesc, dep);            
            
            setDialogState({
                open: true,
                title: canView ? 'CREW LIST' : 'FLIGHT',
                flight: flightDetails,
                crewList: canView ? legMembers : [],
                jumpList: canView ? jumpSeaters : []
            });            
        } catch (error) {
            setDialogState(prev => ({ ...prev, open: false }));
        }
    }, [canView]);
    
    const handleCloseCrewListDialog = useCallback(() => {
        setDialogState(prev => ({ ...prev, open: false }));
    }, []);
    

    /* -------------------- DND -------------------- */
    const sensors = useSensors(
        useSensor(MouseSensor, { 
            activationConstraint: { distance: 5 } 
        }),
        useSensor(TouchSensor, {
            // Trì hoãn 250ms trước khi bắt đầu kéo để dành chỗ cho click/long press
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        })
    );
    

    /* -------------------- SCROLL SYNC -------------------- */
    const handleScroll = useCallback((e) => {
        syncX(e.target, [
            timelineScrollRef.current,
            bottomRightScrollRef.current,
        ]);
        syncY(e.target, [
            topLeftScrollRef.current,
        ]);
    }, [syncX, syncY, timelineScrollRef, topLeftScrollRef, bottomRightScrollRef]);


    /* -------------------- AUTO SCROLL TO NOW -------------------- */
    useEffect(() => {
        if (hasAutoScrolled.current) return;
        if (!baseDate || !cellWidth || !datesCount) return;

        const now = timeMode === 'UTC' ? toZonedTime(new Date(), 'UTC') : new Date();
        const diffHours = (now.getTime() - baseDate.getTime()) / (1000 * 60 * 60);
        if (diffHours < 0 || diffHours > datesCount * 24) return;

        const container = topRightScrollRef.current;
        if (!container) return;

        const scrollToNow = () => {
            if (hasAutoScrolled.current) return;
            const maxScrollLeft = container.scrollWidth - container.clientWidth;
            if (maxScrollLeft <= 0) return; // content not sized yet
            hasAutoScrolled.current = true;
            const target = diffHours * cellWidth - container.clientWidth / 2;
            container.scrollLeft = Math.max(0, Math.min(target, maxScrollLeft));
        };

        // Try immediately; if content isn't sized yet, wait via ResizeObserver
        scrollToNow();
        if (hasAutoScrolled.current) return;

        const observer = new ResizeObserver(scrollToNow);
        observer.observe(container);
        return () => observer.disconnect();
    }, [baseDate, cellWidth, datesCount, timeMode, topRightScrollRef]);


    /* -------------------- ROUTE MAP -------------------- */
    const routeMap = useMemo(() => {
        if (!isShownCrewRoutes) return new Map();
        const map = new Map();

        localData?.forEach(ac => {
            ac.flights?.forEach(f => {
                f.crewRoutes?.forEach(route => {
                    if (!route.routeNo || !route.routeDate) return;
                    const key = `${route.routeDate}_${route.routeNo}`;
                    if (!map.has(key)) map.set(key, []);
                    map.get(key).push(f);
                });
            });
        });

        map.forEach(list => list.sort((a, b) => a.startSlot - b.startSlot));
        return map;
    }, [localData, isShownCrewRoutes]);

    // map objectId -> rowIndex
    const rowIndexMap = useMemo(() => {
        if (!isShownCrewRoutes) return new Map();

        const map = new Map();
        localData?.forEach((ac, rowIdx) => {
            ac.flights?.forEach(f => {
                map.set(f.id, rowIdx);
            });
        });
        return map;
    }, [localData, isShownCrewRoutes]);


    return (
        <DndContext 
            sensors={sensors} 
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%', 
                overflow: 'hidden',
            }}>
                {/* Timeline */}
                <Box sx={{ height: rowHeight, flexShrink: 0, overflow: 'hidden' }}>
                    <Timeline                        
                        cellWidth={cellWidth}
                        rowHeight={rowHeight}
                        from={startDate}
                        to={endDate ? addDays(endDate, 1) : null}
                        ref={timelineScrollRef}
                    />
                </Box>

                {/* Grid */}
                <Box
                    ref={topRightScrollRef}
                    onMouseLeave={onWheelEnd}
                    onTouchEnd={onWheelEnd}
                    onScroll={handleScroll}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onClick={handleBackgroundClick}
                    sx={{
                        flexGrow: 1,
                        overflow: 'auto',
                        position: 'relative',
                        bgcolor: "background.default",
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: gridDimensions.width,
                            height: gridDimensions.height,
                        }}
                    >
                        <NowLine
                            baseDate={baseDate}
                            datesCount={datesCount}
                            cellWidth={cellWidth}
                            height={gridDimensions.height}
                        />

                        {isShownCrewRoutes && routeMap instanceof Map && routeMap.size > 0 && (
                            <RouteLinks
                                cellWidth={cellWidth}
                                rowHeight={rowHeight}
                                routeMap={routeMap}
                                rowIndexMap={rowIndexMap}
                                rowCount={rowCount}
                            />
                        )}

                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: 'none',
                                backgroundImage: `
                                    repeating-linear-gradient(
                                        to right,
                                        ${theme.palette.divider},
                                        ${theme.palette.divider} 1px,
                                        transparent 1px,
                                        transparent ${cellWidth}px
                                    )
                                `,
                            }}
                        />

                        
                        {localData?.map((row) => (
                            <DroppableRow
                                key={row.id}
                                objectId={row.id}
                                rowHeight={rowHeight}
                            >                                
                                {row.flights?.map(f =>                                     
                                    <DraggableObject
                                        key={f.id}
                                        object={f}
                                        cellWidth={cellWidth}
                                        rowHeight={rowHeight}
                                        selected={selectedIds.has(f.id)}
                                        isChanged={changedIds?.has(f.id) ?? false}
                                        onClick={handleObjectClick}
                                        onDoubleClick={handleOpenCrewListDialog}
                                        onMouseEnter={(e) => handleMouseEnter(e, f)}
                                        onMouseLeave={handleMouseLeave}
                                    />                  
                                )}
                            </DroppableRow>
                        ))}

                        {/* KHUNG VẼ VÙNG CHỌN */}
                        {selectionRect && (
                            <Box sx={{
                                position: 'absolute',
                                zIndex: 10,
                                border: '1px solid #1976d2',
                                backgroundColor: 'rgba(25, 118, 210, 0.15)',
                                left: Math.min(selectionRect.x1, selectionRect.x2),
                                top: Math.min(selectionRect.y1, selectionRect.y2),
                                width: Math.abs(selectionRect.x2 - selectionRect.x1),
                                height: Math.abs(selectionRect.y2 - selectionRect.y1),
                                pointerEvents: 'none' 
                            }} />
                        )}
                    </Box>
                </Box>
                
                <Popper
                    open={Boolean(displayFlight) && !isInteracting}
                    anchorEl={finalAnchor}
                    placement="top"
                    transition
                    style={{ zIndex: 11, pointerEvents: 'none' }} 
                    modifiers={[
                        {
                            name: 'offset',
                            options: {                                    
                                offset: [0, 4], 
                            },
                        },
                    ]}
                >
                    {({ TransitionProps }) => (
                        <Fade {...TransitionProps} timeout={250}>
                            <Box sx={{ 
                                px: 1, 
                                py: 0.5, 
                                border: `1px solid ${theme.palette.neutral.deep}`,
                                borderRadius: 1,
                                bgcolor: 'background.paper', 
                            }}>
                                {displayFlight && <PopperContent flight={displayFlight} />}
                            </Box>
                        </Fade>
                    )}
                </Popper>                
            </Box>
            
            {/* --- DRAG OVERLAY --- */}           
            <DragOverlay adjustScale={false}>
                {draggingId && activeFlight && (
                    <Box
                        sx={{
                            pointerEvents: 'none',
                            opacity: 0.9,
                        }}
                    >
                        {/* KÉO NHÓM */}
                        {selectedIds.has(draggingId) && selectedIds.size > 1 ? (
                            <Box sx={{ position: 'relative' }}>
                                {/* Shadow layer */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: -6,
                                        bgcolor: theme.palette.primary.main,
                                        opacity: 0.15,
                                        borderRadius: 2,
                                        transform: 'rotate(-2deg)',
                                    }}
                                />

                                {/* Ghost chính */}
                                <Box
                                    sx={{
                                        width: activeFlight.duration * cellWidth,
                                        height: rowHeight - 8,
                                        background: theme.palette.primary.light,
                                        borderRadius: 1,
                                        border: '1px solid rgba(255,255,255,0.6)',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        px: 1.5,
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                    }}
                                >
                                    <Box pr={0.5}>
                                        <span>Moving</span>
                                    </Box>                                    
                                    <Box
                                        sx={{
                                            minWidth: 18,
                                            height: 18,                                            
                                            bgcolor: '#ff9800',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.7rem',
                                            color: '#000',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {selectedIds.size}
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            /* KÉO 1 OBJECT */
                            <Box
                                sx={{
                                    width: activeFlight.duration * cellWidth,
                                    height: rowHeight - 8,
                                    background: theme.palette.primary.light,
                                    borderRadius: 1,
                                    border: '1px solid rgba(255,255,255,0.6)',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                }}
                            >
                                {activeFlight.flightNo}
                            </Box>
                            // <GanttBar object={activeFlight} />
                        )}
                    </Box>
                )}
            </DragOverlay>
            
            {dialogState.open && 
                <CrewListDialog 
                    open={dialogState.open}
                    onClose={handleCloseCrewListDialog}
                    title={dialogState.title}
                    flight={dialogState.flight}
                    crewList={dialogState.crewList}
                    jumpList={dialogState.jumpList}
                />
            }
        </DndContext>
    );
};

export default React.memo(RightPanel);
